import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';

import environment from '@/environment';

const reloadPage = () => {
  if (environment.CYPRESS) return;
  window.location.reload();
};

export const getExplorerVersion = async (isVerticalSlice: boolean): Promise<string> => {
  const response = await axios.get<any>(
    isVerticalSlice
      ? `${window.location.origin}/explore/internal-versions.json` // This the current tenant's version of the explorer app
      : `${window.location.origin}/assets/explorer-version.txt`, // This is cloud-ui's version of the explorer package
    {
      headers: {
        'Content-Type': 'text/plain; charset=utf-8',
        Authorization: null,
        Accept:
          'text/html,application/xhtml+xml,application/xml;q=0.9,image/avif,image/webp,image/apng,*/*;q=0.8,application/signed-exchange;v=b3;q=0.7',
      },
    },
  );

  return response.data;
};

/** 30 Minutes */
const REFETCH_INTERVAL_MILLIS = 30 * 60 * 1000;

type CloudUIExplorerVersionProps = {
  isVerticalSlice?: boolean;
  enabled?: boolean;
};

const useCloudUiExplorerVersion = ({
  isVerticalSlice = false,
  enabled = true,
}: CloudUIExplorerVersionProps) => {
  return useQuery<any, AxiosError>({
    queryKey: ['cloud-ui-explorer-version'],
    queryFn: () => getExplorerVersion(isVerticalSlice),
    enabled: enabled && !environment.CYPRESS,
    refetchInterval: REFETCH_INTERVAL_MILLIS,
    refetchOnWindowFocus: true,
    retry: false,
  });
};

/**
 * If we ever detect a change in the sha, we should reload the page.
 */
export const useReloadPage = (isVerticalSlice: boolean) => {
  const currentVersion = useRef<string | null | undefined>(undefined);

  const explorerVersion = useCloudUiExplorerVersion({ isVerticalSlice }).data;
  useEffect(() => {
    if (!explorerVersion) return;
    if (
      currentVersion.current &&
      explorerVersion &&
      currentVersion.current !== explorerVersion
    ) {
      reloadPage();
    }
    currentVersion.current = explorerVersion;
  }, [explorerVersion]);
};
